import React, { useEffect, Children, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FlameIcon, ZapIcon, HeartIcon, LeafIcon, AlertCircleIcon, ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { CTAButton } from '../components/CTAButton';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { EbookMockup } from '../components/EbookMockup';
import { TestimonialCard } from '../components/TestimonialCard';
import { BenefitItem } from '../components/BenefitItem';
import { Footer } from '../components/Footer';
import { FAQSection } from '../components/FAQSection';
export function LandingPage() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    (window as any).pixelId = '69bf106e9f74e8dc649360a2';
    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('defer', '');
    script.setAttribute('src', 'https://cdn.utmify.com.br/scripts/pixel/pixel.js');
    document.head.appendChild(script);
    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  const handlePurchase = () => {
    // Fire Utmify AddToCart event
    try {
      const w = window as any;
      if (w.Utmify && typeof w.Utmify.track === 'function') {
        w.Utmify.track('AddToCart');
      }
    } catch (e) {
      // Silently fail if pixel not loaded
    }
    window.location.href = 'https://www.saborybienestar.online/products/recetas-anti-inflamatorias';
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-75">
      {/* HERO SECTION */}
      <section className="relative pt-16 pb-16 px-4 bg-wellness-cream overflow-hidden" data-id="element-76">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-40 pointer-events-none" data-id="element-77">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-wellness-light rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-78"></div>
          <div className="absolute top-48 -left-24 w-72 h-72 bg-wellness-gold rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-79"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-80">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} data-id="element-81">
            <span className="inline-block py-1 px-3 rounded-full bg-wellness-primary/10 text-wellness-primary font-semibold text-sm mb-6 tracking-wide uppercase" data-id="element-82">
              Nuevo Programa 2026
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-83">
              ¡En Menos de 7 Días te Sentirás con Menos Hinchazón!
            </h1>
            <p className="text-xl md:text-2xl text-wellness-body mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-84">
              Reduce la hinchazón, mejora tu digestión y recupera tu energía con
              recetas simples y deliciosas que cualquier persona puede preparar
            </p>

            <div className="flex flex-col items-center justify-center space-y-6" data-id="element-85">
              <CTAButton onClick={() => {
              document.getElementById('whats-included')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="text-xl px-10 py-5" data-id="element-86">
                ¡Quiero Empezar Ahora!
              </CTAButton>
              <CountdownTimer data-id="element-87" />
              <p className="text-sm text-wellness-body flex items-center" data-id="element-88">
                <AlertCircleIcon className="w-4 h-4 mr-1 text-wellness-red" data-id="element-89" />
                Oferta especial por tiempo limitado
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-14 px-4 bg-white" data-id="element-90">
        <div className="max-w-5xl mx-auto" data-id="element-91">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-10" data-id="element-92">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-93">
              ¿Te identificas con esto?
            </h2>
            <p className="text-lg text-wellness-body max-w-2xl mx-auto" data-id="element-94">
              No estás sola. Miles de mujeres de más de 35 años sufren en
              silencio pensando que es "normal" sentirse así.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center mb-10" data-id="element-95">
            <img src="https://cdn.shopify.com/s/files/1/0673/8326/3309/files/panza_antes_despues_2_1.png?v=1771791492" alt="Resultados antes y después - reducción de hinchazón abdominal" className="w-full max-w-2xl rounded-2xl shadow-lg" data-id="element-96" />
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-97">
            {[{
            icon: <AlertCircleIcon className="w-8 h-8" data-id="element-98" />,
            title: 'Hinchazón después de comer',
            desc: 'Sientes que tu abdomen se infla como un globo incluso comiendo poco.'
          }, {
            icon: <ZapIcon className="w-8 h-8" data-id="element-99" />,
            title: 'Cansancio constante',
            desc: 'Te despiertas sin energía y necesitas café para sobrevivir el día.'
          }, {
            icon: <HeartIcon className="w-8 h-8" data-id="element-100" />,
            title: 'Problemas digestivos',
            desc: 'Tránsito lento, pesadez o acidez que arruinan tus comidas favoritas.'
          }, {
            icon: <FlameIcon className="w-8 h-8" data-id="element-101" />,
            title: 'Inflamación crónica',
            desc: 'Dolores articulares, retención de líquidos y dificultad para perder peso.'
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-wellness-beige p-8 rounded-2xl flex items-start space-x-4" data-id="element-102">
                <div className="text-wellness-red bg-white p-3 rounded-xl shadow-sm" data-id="element-103">
                  {item.icon}
                </div>
                <div data-id="element-104">
                  <h3 className="text-xl font-bold mb-2" data-id="element-105">{item.title}</h3>
                  <p className="text-wellness-body" data-id="element-106">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* AGITATION SECTION */}
      <section className="py-14 px-4 bg-wellness-dark text-white text-center" data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-109">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-110">
              Ignorar estos síntomas tiene un costo...
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-8" data-id="element-111">
              La inflamación crónica es la raíz de la mayoría de los problemas
              de salud modernos. Ignorarla significa depender cada vez más de
              medicamentos, perderte momentos valiosos por falta de energía, y
              aceptar que sentirte "mal" es tu nueva normalidad.{' '}
              <strong className="text-wellness-gold" data-id="element-112">
                Pero no tiene que ser así.
              </strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-14 px-4 bg-wellness-cream" data-id="element-113">
        <div className="max-w-5xl mx-auto" data-id="element-114">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-115">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-116">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-117">
                Imagina una vida sin inflamación...
              </h2>
              <p className="text-lg text-wellness-body mb-8" data-id="element-118">
                Al cambiar tu alimentación con nuestras recetas estratégicamente
                diseñadas, tu cuerpo comenzará a sanar desde adentro hacia
                afuera.
              </p>
              <div className="space-y-4" data-id="element-119">
                <BenefitItem text="Más energía desde que te despiertas" data-id="element-120" />
                <BenefitItem text="Digestión ligera y sin molestias" data-id="element-121" />
                <BenefitItem text="Abdomen plano y sin hinchazón" data-id="element-122" />
                <BenefitItem text="Piel más radiante y limpia" data-id="element-123" />
                <BenefitItem text="Mejor calidad de sueño" data-id="element-124" />
                <BenefitItem text="Pérdida de peso natural y saludable" data-id="element-125" />
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative" data-id="element-126">
              <div className="aspect-square rounded-full bg-wellness-light/20 absolute -inset-4 blur-2xl" data-id="element-127"></div>
              <img src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800" alt="Mujer feliz comiendo saludable" className="rounded-2xl shadow-2xl relative z-10 object-cover w-full h-[500px]" data-id="element-128" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="whats-included" className="py-14 px-4 bg-white" data-id="element-129">
        <div className="max-w-6xl mx-auto" data-id="element-130">
          <div className="text-center mb-10" data-id="element-131">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-132">
              Todo lo que recibirás hoy
            </h2>
            <p className="text-lg text-wellness-body" data-id="element-133">
              Un paquete completo diseñado para garantizar tu éxito.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8" data-id="element-134">
            <div className="bg-wellness-beige rounded-2xl p-6 flex flex-col items-center text-center" data-id="element-135">
              <div className="w-48 md:w-64 mx-auto mb-6" data-id="element-136">
                <img src="https://cdn.magicpatterns.com/uploads/cz8xXUAjDkUTUNS6yrV9kS/Mockup_Recetas_Antiinflamatorias.png" alt="Recetas Antiinflamatorias - 30 recetas simples para ayudarte a reducir la inflamación de forma natural" className="w-full h-auto rounded-lg shadow-2xl" data-id="element-137" />
              </div>
              <h3 className="font-bold text-xl mb-2" data-id="element-138">Libro Principal</h3>
              <p className="text-sm text-wellness-body" data-id="element-139">
                30 recetas deliciosas y fáciles de preparar para desinflamar tu
                cuerpo.
              </p>
            </div>

            <div className="bg-wellness-beige rounded-2xl p-6 flex flex-col items-center text-center relative" data-id="element-140">
              <div className="absolute top-4 right-4 bg-wellness-gold text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-141">
                BONUS
              </div>
              <div className="w-48 md:w-64 mx-auto mb-6" data-id="element-142">
                <img src="https://cdn.magicpatterns.com/uploads/9aC6WRoD9WGkLH8cSDQumP/Mockup_Batidos_Saludables.png" alt="Batidos Saludables - Deliciosos y sin azúcar, aumentarán tu energía y bienestar" className="w-full h-auto rounded-lg shadow-2xl" data-id="element-143" />
              </div>
              <h3 className="font-bold text-xl mb-2" data-id="element-144">Ebook de Batidos</h3>
              <p className="text-sm text-wellness-body" data-id="element-145">
                30 Recetas de Batidos Saludables y Nutritivos para desayunos
                rápidos llenos de energía.
              </p>
            </div>

            <div className="bg-wellness-beige rounded-2xl p-6 flex flex-col items-center text-center relative" data-id="element-146">
              <div className="absolute top-4 right-4 bg-wellness-gold text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-147">
                BONUS
              </div>
              <div className="w-48 md:w-64 mx-auto mb-6" data-id="element-148">
                <img src="https://cdn.shopify.com/s/files/1/0673/8326/3309/files/02_Lista_de_Compras_Antiinflamatoria_Premium_Mockup.png?v=1773313507" alt="Lista de Compras Antiinflamatorias - Guía de compras para tu programa" className="w-full h-auto rounded-lg shadow-2xl" data-id="element-149" />
              </div>
              <h3 className="font-bold text-xl mb-2" data-id="element-150">Lista de Compras</h3>
              <p className="text-sm text-wellness-body" data-id="element-151">
                Guía exacta de qué comprar en el supermercado/ dietética para no
                perder tiempo.
              </p>
            </div>

            <div className="bg-wellness-beige rounded-2xl p-6 flex flex-col items-center text-center relative" data-id="element-152">
              <div className="absolute top-4 right-4 bg-wellness-gold text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-153">
                BONUS
              </div>
              <div className="w-48 md:w-64 mx-auto mb-6" data-id="element-154">
                <img src="https://cdn.shopify.com/s/files/1/0673/8326/3309/files/04_Planificador_Semanal_Antiinflamatorio_mockup.png?v=1773313507" alt="Planificador Semanal - Organiza tus comidas de la semana" className="w-full h-auto rounded-lg shadow-2xl" data-id="element-155" />
              </div>
              <h3 className="font-bold text-xl mb-2" data-id="element-156">Planificador Semanal</h3>
              <p className="text-sm text-wellness-body" data-id="element-157">
                Organiza tus comidas de la semana para mantener la constancia
                sin estrés.
              </p>
            </div>

            <div className="bg-wellness-beige rounded-2xl p-6 flex flex-col items-center text-center relative" data-id="element-158">
              <div className="absolute top-4 right-4 bg-wellness-gold text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-159">
                BONUS
              </div>
              <div className="w-48 md:w-64 mx-auto mb-6" data-id="element-160">
                <img src="https://cdn.shopify.com/s/files/1/0673/8326/3309/files/03_Manual_Maestro_de_Sustituciones_Antiinflamatorias_Mockup.png?v=1773313507" alt="Manual Maestro de Sustituciones Antiinflamatorias - Transforma cualquier receta" className="w-full h-auto rounded-lg shadow-2xl" data-id="element-161" />
              </div>
              <h3 className="font-bold text-xl mb-2" data-id="element-162">
                Manual Maestro de Sustituciones Antiinflamatorias
              </h3>
              <p className="text-sm text-wellness-body" data-id="element-163">
                Transforma cualquier receta en una versión que desinflama tu
                cuerpo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICE / VALUE STACK */}
      <section className="py-14 px-4 bg-wellness-cream" data-id="element-164">
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-wellness-darkBeige" data-id="element-165">
          <div className="bg-wellness-primary p-6 text-center text-white" data-id="element-166">
            <h2 className="text-2xl font-bold" data-id="element-167">Resumen de tu Inversión</h2>
          </div>
          <div className="p-8 md:p-12" data-id="element-168">
            <ul className="space-y-4 mb-8" data-id="element-169">
              <li className="flex justify-between items-center border-b border-gray-100 pb-4" data-id="element-170">
                <span className="font-medium text-[14px]" data-id="element-171">
                  Ebook Recetas Antiinflamatorias
                </span>
                <span className="text-wellness-body text-[14px] font-[400]" data-id="element-172">
                  Valor: $30.000 ARS
                </span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-100 pb-4" data-id="element-173">
                <span className="font-medium text-[14px]" data-id="element-174">
                  BONUS 1: Batidos Saludables
                </span>
                <span className="text-wellness-body text-[14px]" data-id="element-175">
                  Valor: $25.000 ARS
                </span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-100 pb-4" data-id="element-176">
                <span className="font-medium text-[14px]" data-id="element-177">
                  BONUS 2: Lista de Compras Premium
                </span>
                <span className="text-wellness-body text-[14px]" data-id="element-178">
                  Valor: $10.000 ARS
                </span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-100 pb-4" data-id="element-179">
                <span className="font-medium text-[14px]" data-id="element-180">
                  BONUS 3: Planificador Semanal
                </span>
                <span className="text-wellness-body text-[14px]" data-id="element-181">
                  Valor: $10.000 ARS
                </span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-100 pb-4" data-id="element-182">
                <span className="font-medium text-[14px]" data-id="element-183">
                  BONUS 4: Manual de Sustituciones
                </span>
                <span className="text-wellness-body text-[14px]" data-id="element-184">
                  Valor: $15.000 ARS
                </span>
              </li>
            </ul>

            <div className="text-center mb-8" data-id="element-185">
              <p className="text-wellness-body mb-2" data-id="element-186">
                Valor Total: <span className="line-through" data-id="element-187">$90.000 ARS</span>
              </p>
              <div className="inline-block bg-wellness-light/20 text-wellness-primary font-bold px-4 py-1 rounded-full text-sm mb-4" data-id="element-188">
                Ahorras más del $75.000 ARS
              </div>
              <p className="text-xl mb-1" data-id="element-189">Hoy solo pagas:</p>
              <p className="font-bold text-wellness-primary text-[40px]" data-id="element-190">
                $14.900 ARS
              </p>
            </div>

            <CTAButton onClick={handlePurchase} className="w-full" data-id="element-191">
              ¡Sí, Quiero Mi Programa!
            </CTAButton>
            <div className="mt-4 flex justify-center" data-id="element-192">
              <CountdownTimer data-id="element-193" />
            </div>
          </div>
        </div>
      </section>

      {/* RECIPE PHOTOS */}
      <section className="py-14 px-4 bg-white" data-id="element-194">
        <div className="max-w-6xl mx-auto" data-id="element-195">
          <div className="text-center mb-10" data-id="element-196">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-197">
              Un vistazo a las deliciosas recetas que te esperan
            </h2>
            <p className="text-lg text-wellness-body" data-id="element-198">
              Comer sano nunca fue tan sabroso.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8" data-id="element-199">
            {[{
            img: 'https://cdn.shopify.com/s/files/1/0673/8326/3309/files/ensalada_de_quinoa_y_palta.png?v=1773000754',
            label: 'Ensaladas Nutritivas'
          }, {
            img: 'https://cdn.shopify.com/s/files/1/0673/8326/3309/files/ensalada_de_fruta_con_chia.png?v=1773000754',
            label: 'Desayunos Energéticos'
          }, {
            img: 'https://cdn.shopify.com/s/files/1/0673/8326/3309/files/pollo_al_limon.png?v=1773000755',
            label: 'Platos Principales'
          }, {
            img: 'https://cdn.shopify.com/s/files/1/0673/8326/3309/files/sopa.png?v=1773000754',
            label: 'Sopas Reconfortantes'
          }].map((item, i) => <div key={i} className="relative group overflow-hidden rounded-2xl shadow-md aspect-square" data-id="element-200">
                <img src={item.img} alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-201" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4 md:p-6" data-id="element-202">
                  <span className="text-white font-medium text-sm md:text-lg" data-id="element-203">
                    {item.label}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-14 px-4 bg-wellness-beige" data-id="element-204">
        <div className="max-w-6xl mx-auto" data-id="element-205">
          <div className="text-center mb-10" data-id="element-206">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-207">
              Lo que dicen nuestras clientas
            </h2>
            <p className="text-lg text-wellness-body" data-id="element-208">
              Historias reales de transformación.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-209">
            <TestimonialCard name="María G." age={42} quote="Llevaba años sintiéndome hinchada después de cada comida. Con estas recetas, en solo una semana mi abdomen se desinflamó y tengo mucha más energía. ¡Son súper fáciles de hacer!" data-id="element-210" />
            <TestimonialCard name="Carmen L." age={55} quote="Mi digestión mejoró un 100%. Ya no dependo de antiácidos y he descubierto sabores increíbles. Lo mejor es que los ingredientes son fáciles de encontrar en cualquier supermercado o dietética." data-id="element-211" />
            <TestimonialCard name="Ana R." age={38} quote="Pensé que comer sano significaba pasar hambre o comer aburrido. Este libro me demostró lo contrario. Me siento más ligera, duermo mejor y hasta mi piel luce más brillante." data-id="element-212" />
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-14 px-4 bg-white" data-id="element-213">
        <GuaranteeSection data-id="element-214" />
      </section>

      {/* FAQ */}
      <FAQSection data-id="element-215" />

      {/* FINAL CTA */}
      <section className="py-16 px-4 bg-wellness-cream text-center" data-id="element-216">
        <div className="max-w-3xl mx-auto" data-id="element-217">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-218">
            ¿Lista para transformar tu salud?
          </h2>
          <p className="text-2xl text-wellness-body mb-8" data-id="element-219">
            Todo por solo{' '}
            <strong className="text-wellness-primary" data-id="element-220">$14.900 ARS</strong>
          </p>

          <div className="flex flex-col items-center justify-center space-y-6" data-id="element-221">
            <CTAButton onClick={handlePurchase} className="text-xl px-10 py-5 w-full md:w-auto" data-id="element-222">
              ¡Sí, Quiero Mi Programa Antiinflamatorio!
            </CTAButton>
            <CountdownTimer data-id="element-223" />
            <p className="text-sm text-wellness-body flex items-center justify-center space-x-4 mt-4" data-id="element-224">
              <span className="flex items-center" data-id="element-225">
                <ShieldCheckIcon className="w-4 h-4 mr-1" data-id="element-226" /> Pago seguro
              </span>
              <span className="flex items-center" data-id="element-227">
                <ZapIcon className="w-4 h-4 mr-1" data-id="element-228" /> Acceso inmediato
              </span>
              <span className="flex items-center" data-id="element-229">
                <HeartIcon className="w-4 h-4 mr-1" data-id="element-230" /> Garantía 7 días
              </span>
            </p>
            <div className="mt-6" data-id="element-231">
              <img src="https://cdn.shopify.com/s/files/1/0673/8326/3309/files/Pago_seguro_garantizado.png?v=1771789861" alt="Métodos de pago aceptados - Pago seguro garantizado" className="max-w-xs md:max-w-sm mx-auto w-full h-auto" data-id="element-232" />
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-233" />
    </div>;
}